import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { ProductResource } from '@menu/Types/Resources/ProductResource';
import { ProductVariantResource } from '@menu/Types/Resources/ProductVariantResource';
import { ModifierResource } from '@menu/Types/Resources/ModifierResource';

import { isEqual } from 'lodash';
import getRestaurant from '@menu/Utils/getRestaurant';

export type CartItem = {
  product: ProductResource;
  variant: ProductVariantResource;
  modifiers: ModifierResource[];
  price: number;
  quantity: number;
};

export const useCartStore = defineStore('cart', () => {
  const cartItemsIds = ref<
    {
      productId: number;
      variantId: number;
      modifierIds: number[];
      quantity: number;
    }[]
  >(
    (() => {
      try {
        // it'll throw an exception if the structure is invalid
        return [...JSON.parse(localStorage.getItem('cartItemsIds') ?? '[]')];
      } catch (e) {
        // because sometimes `SecurityError: The operation is insecure.` happens
        // ...or because data is invalid for whatever reason
        return [];
      }
    })(),
  );

  watch(cartItemsIds, () => {
    try {
      localStorage.setItem('cartItemsIds', JSON.stringify(cartItemsIds.value));
    } catch (e) {
      // because sometimes `SecurityError: The operation is insecure.` happens
    }
  });

  const restaurant = getRestaurant();

  const cartItems = computed(() => {
    const cartItems: CartItem[] = [];

    cartItemsIds.value.forEach((cartItemIds) => {
      restaurant.menus.forEach((menu) => {
        menu.categories.forEach((category) => {
          category.products.forEach((product) => {
            product.variants.forEach((variant) => {
              if (
                cartItemIds.productId === product.id &&
                cartItemIds.variantId === variant.id
              ) {
                const modifiers: ModifierResource[] = [];

                menu.modifierSets.forEach((modifierSet) => {
                  modifierSet.modifiers.forEach((modifier) => {
                    if (cartItemIds.modifierIds.includes(modifier.id)) {
                      modifiers.push(modifier);
                    }
                  });
                });

                let price = 0;

                if (variant.price !== null) {
                  price += variant.price;
                }

                modifiers.forEach((modifier) => {
                  if (modifier.price !== null) {
                    price += modifier.price;
                  }
                });

                cartItems.push({
                  product,
                  variant,
                  modifiers,
                  price,
                  quantity: cartItemIds.quantity,
                });
              }
            });
          });
        });
      });
    });

    return cartItems;
  });

  const cartItemsTotalPrice = computed(() => {
    let totalPrice = 0;

    cartItems.value.forEach((cartItem) => {
      totalPrice += cartItem.price * cartItem.quantity;
    });

    return totalPrice;
  });

  const cartItemsTotalQuantity = computed(() => {
    let totalQuantity = 0;

    cartItems.value.forEach((cartItem) => {
      totalQuantity += cartItem.quantity;
    });

    return totalQuantity;
  });

  const setCartItemQuantity = ({
    productId,
    variantId,
    modifierIds,
    quantity,
  }: {
    productId: number;
    variantId: number;
    modifierIds: number[];
    quantity: number;
  }) => {
    // We use `getters.cartItems` because it contains the latest data
    // where modifiers that don't exist anymore are excluded. It is
    // useful for matching cart items by `modifierIds`.
    const pendingCartItemsIds = cartItems.value.map((cartItem) => {
      return {
        productId: cartItem.product.id,
        variantId: cartItem.variant.id,
        modifierIds: cartItem.modifiers.map((modifier) => modifier.id),
        quantity: cartItem.quantity,
      };
    });

    const existingCartItem = pendingCartItemsIds.find(
      (cartItem) =>
        cartItem.productId === productId &&
        cartItem.variantId === variantId &&
        isEqual(cartItem.modifierIds, modifierIds),
    );

    if (existingCartItem) {
      existingCartItem.quantity = quantity;
    } else {
      pendingCartItemsIds.push({
        productId,
        variantId,
        modifierIds,
        quantity,
      });
    }

    cartItemsIds.value = pendingCartItemsIds.filter(
      ({ quantity }) => quantity !== 0,
    );
  };

  const clearCartItems = () => {
    cartItemsIds.value = [];
  };

  return {
    cartItemsIds,
    cartItems,
    cartItemsTotalPrice,
    cartItemsTotalQuantity,
    setCartItemQuantity,
    clearCartItems,
  };
});
